import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import { ExclamationIcon, ExternalLinkIcon } from '@heroicons/react/outline';
import { AdminCourseLmsStatus, LmsCourseContentDTO } from '@shared/apis/courses.apis';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import {
  FlagPill,
  LMS_BASE_URL,
  LMS_STATUS_STYLES,
  LmsStatusBadge,
  formatUnitDuration,
  formatUnitTypes,
  lmsAccessLabel,
  unitTypeIcon,
} from './courseUi';

type Props = {
  status: AdminCourseLmsStatus;
  lms: LmsCourseContentDTO | null;
  lmsCourseId?: string;
  error?: string;
  isLoading?: boolean;
  pendingLmsCourseId?: string;
};

const LmsContentPanel = (props: Props) => {
  const { status, lms, lmsCourseId, error, isLoading, pendingLmsCourseId } = props;

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center rounded-2xl bg-surfaceObject">
        <LoadingSpinner size="normal" />
      </div>
    );
  }

  const linkChanged =
    pendingLmsCourseId !== undefined && pendingLmsCourseId !== (lmsCourseId ?? '');

  return (
    <div className="flex flex-col gap-4">
      {linkChanged ? (
        <div className="flex items-start gap-3 rounded-2xl bg-customGold/10 p-4 text-sm text-customGold">
          <ExclamationIcon className="mt-0.5 h-5 w-5 shrink-0" />
          <span>
            The LMS link below reflects the saved course. Save your changes to see the content of
            the newly selected LMS course.
          </span>
        </div>
      ) : null}

      <div className="flex flex-col gap-4 rounded-2xl bg-surfaceObject p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <LmsStatusBadge status={status} />
              {lms?.access ? <FlagPill>{lmsAccessLabel(lms.access)}</FlagPill> : null}
              {lms ? <FlagPill>{lms.price ? `${lms.price}$` : 'Free in LMS'}</FlagPill> : null}
            </div>
            <span className="text-sm text-customGrey">{LMS_STATUS_STYLES[status].description}</span>
            {lms?.modifiedAt ? (
              <span className="text-xs text-customGrey">
                Last changed in the LMS {moment(lms.modifiedAt).fromNow()} · checked{' '}
                {moment(lms.fetchedAt).fromNow()}
              </span>
            ) : null}
          </div>
          {lmsCourseId ? (
            <a
              className="inline-flex items-center gap-1.5 text-sm text-customPrimary hover:underline"
              href={`${LMS_BASE_URL}/path-player?courseid=${lmsCourseId}`}
              target="_blank"
              rel="noreferrer"
            >
              Open in LMS <ExternalLinkIcon className="h-4 w-4" />
            </a>
          ) : null}
        </div>

        {error ? (
          <div className="rounded-xl bg-customRed/10 p-3 text-xs text-customRed">{error}</div>
        ) : null}

        {lms ? (
          <div className="flex flex-col gap-1 text-sm text-customLight">
            <span className="font-semibold text-white">{lms.title}</span>
            <span className="text-xs text-customGrey">
              {lms.sectionCount} section{lms.sectionCount === 1 ? '' : 's'} · {lms.unitCount}{' '}
              learning unit{lms.unitCount === 1 ? '' : 's'}
              {lms.unitCount ? ` · ${formatUnitTypes(lms.unitsByType)}` : ''}
            </span>
          </div>
        ) : null}
      </div>

      {lms && lms.sections.length ? (
        <div className="flex flex-col gap-3">
          {lms.sections.map((section, index) => (
            <div key={section.id} className="flex flex-col rounded-2xl bg-surfaceObject">
              <div className="flex items-center justify-between gap-3 px-5 py-3">
                <span className="text-sm font-semibold text-white">
                  <span className="mr-2 text-customGrey">{index + 1}.</span>
                  {section.title}
                </span>
                <span
                  className={mergeClassNames(
                    'text-xs',
                    section.units.length ? 'text-customGrey' : 'text-customRed'
                  )}
                >
                  {section.units.length
                    ? `${section.units.length} unit${section.units.length === 1 ? '' : 's'}`
                    : 'empty section'}
                </span>
              </div>
              {section.units.length ? (
                <ul className="flex flex-col border-t border-white/5">
                  {section.units.map((unit) => (
                    <li
                      key={unit.id}
                      className="flex items-center gap-3 border-b border-white/5 px-5 py-2 text-sm last:border-b-0"
                    >
                      <span className="text-customGrey">{unitTypeIcon(unit.type)}</span>
                      <span className="min-w-0 flex-1 truncate text-customLight">{unit.title}</span>
                      <span className="shrink-0 text-xs text-customGrey">
                        {unit.type.replace(/_/g, ' ')}
                        {unit.duration ? ` · ${formatUnitDuration(unit.duration)}` : ''}
                        {unit.isFree ? ' · free' : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default LmsContentPanel;
